import {
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";

import { RequestPayload, sendRequest } from "../../api/medical/request";
import { capture } from "../../shared/capture";
import { Actions, Analytics, Features } from "../shared/analytics";
import { Button } from "../shared/Button";
import DragAndDrop from "../shared/DragAndDrop";

type MedicalRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormProps = {
  event: React.FormEvent;
  title: string;
  fullName: string;
  phone: string;
  email: string;
  baaFile: File | null;
  noBaaCheck: boolean;
};

const MedicalRequestModal = ({ isOpen, onClose }: MedicalRequestModalProps) => {
  const [isSending, setSending] = useState(false);
  const [noFileError, setNoFileError] = useState<string | null>(null);

  const readFileAsBase64 = (file: File): Promise<FileReader["result"]> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const handleSubmit = async ({
    event,
    title,
    fullName,
    phone,
    email,
    baaFile,
    noBaaCheck,
  }: FormProps) => {
    setSending(true);
    event.preventDefault();

    if (!baaFile && !noBaaCheck) {
      setNoFileError("BAA is required.");
      return;
    }

    try {
      const payload: RequestPayload = {
        title,
        fullName,
        phone,
        email,
      };

      if (baaFile) {
        const fileBase64 = await readFileAsBase64(baaFile);

        if (typeof fileBase64 !== "string") {
          throw Error("Invalid base64 type");
        }

        payload.file = fileBase64;
      }

      await sendRequest(payload);

      Analytics.emit(Actions.submit, Features.form, { type: Features.medicalRequest, ...payload });

      onClose();
      alert(
        "Thank you for you interest in our Medical API. We will be in contact with you shortly regarding the status of your request."
      );

      setSending(false);
    } catch (err) {
      capture.error(err, { extra: { title, fullName, phone, email, context: `mapi.request` } });
      onClose();
      alert("There seems to have been an error submitting your request.");
      setSending(false);
    }
  };

  const onModalClose = () => {
    Analytics.emit(Actions.close, Features.form, { type: Features.medicalRequest });
    onClose();
  };

  return (
    <VStack align="stretch">
      <Modal isOpen={isOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent maxW={{ base: "90vw", xl: "50vw" }} pb={5}>
          <ModalHeader>Request Access</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <MedicalRequestForm
              handleSubmit={handleSubmit}
              isSending={isSending}
              setNoFileError={setNoFileError}
              noFileError={noFileError}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

type MedicalRequestFormProps = {
  handleSubmit: (props: FormProps) => void;
  isSending: boolean;
  setNoFileError: (err: string | null) => void;
  noFileError: string | null;
};

const MedicalRequestForm = ({
  handleSubmit,
  isSending,
  setNoFileError,
  noFileError,
}: MedicalRequestFormProps) => {
  const [title, setTitle] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [baaFile, setBaafile] = useState<File | null>(null);
  const [noBaaCheck, setNoBaaCheck] = useState<boolean>(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      setBaafile(acceptedFiles[0]);
      setNoFileError(null);
    } else {
      setBaafile(null);
      setNoFileError("Invalid file type");
    }
  }, []);

  return (
    <form
      onSubmit={event =>
        handleSubmit({ event, title, fullName, phone, email, baaFile, noBaaCheck })
      }
    >
      <FormControl mb={5} isRequired>
        <FormLabel>Title</FormLabel>
        <Input type="text" onChange={event => setTitle(event.currentTarget.value)} />
      </FormControl>
      <FormControl mb={5} isRequired>
        <FormLabel>Full Name</FormLabel>
        <Input type="text" onChange={event => setFullName(event.currentTarget.value)} />
      </FormControl>
      <FormControl mb={5} isRequired>
        <FormLabel>Phone</FormLabel>
        <Input type="tel" onChange={event => setPhone(event.currentTarget.value)} />
      </FormControl>
      <FormControl mb={5} isRequired>
        <FormLabel>Email</FormLabel>
        <Input type="email" onChange={event => setEmail(event.currentTarget.value)} />
      </FormControl>
      <FormControl mb={5} isInvalid={!!noFileError}>
        <FormLabel>Business Associate Agreement</FormLabel>
        <DragAndDrop onDrop={onDrop} fileName={baaFile?.name} />
        {noFileError && <FormErrorMessage>{noFileError}</FormErrorMessage>}
      </FormControl>
      <FormControl mb={5}>
        <Checkbox onChange={event => setNoBaaCheck(event.currentTarget.checked)}>
          I don't have a BAA (if you don't have a BAA, check here and we'll send you one).
        </Checkbox>
      </FormControl>
      <FormControl mb={5} isRequired>
        <Checkbox>
          I confirm that I am authorized to make requests to the Metriport API on behalf of an
          entity with an NPI number for a valid{" "}
          <Link
            href="https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/disclosures-treatment-payment-health-care-operations/index.html"
            textDecoration={"underline"}
            isExternal
          >
            Treatment
          </Link>{" "}
          purpose of use.
        </Checkbox>
      </FormControl>
      <Button width="full" mt={4} isLoading={isSending} type="submit">
        Request Access
      </Button>
    </form>
  );
};

export default MedicalRequestModal;
